import { Autocomplete, Chip, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { filter, join, map } from 'es-toolkit/compat';
import { JobPreset } from 'interfaces/jobPreset';
import React from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';

import {
  CalculateFeaturesPresetGroupContext,
  getCalculateFeaturesPresetFilterText,
  getCalculateFeaturesPresetGroup,
  sortCalculateFeaturesPresetsForAutocomplete,
} from './getCalculateFeaturesPresetGroup';

interface CalculateFeaturesPresetAutocompleteProps {
  presets: JobPreset[];
  selectedPreset: JobPreset | null;
  isLoading?: boolean;
  groupContext: CalculateFeaturesPresetGroupContext;
  stainTypeIdToDisplayName: (stain: string) => string;
  isLoadingStainTypeOptions?: boolean;
  onSelectPreset: (preset: JobPreset) => void;
}

const getPresetOptionLabel = (preset: JobPreset) =>
  `${preset.name} · v${preset.versionNumber}${preset.deletedAt ? ' (archived)' : ''}`;

export const CalculateFeaturesPresetAutocomplete: React.FC<CalculateFeaturesPresetAutocompleteProps> = ({
  presets,
  selectedPreset,
  isLoading,
  groupContext,
  stainTypeIdToDisplayName,
  isLoadingStainTypeOptions,
  onSelectPreset,
}) => {
  const options = sortCalculateFeaturesPresetsForAutocomplete(presets, groupContext);

  return (
    <Autocomplete
      options={options}
      value={selectedPreset}
      loading={isLoading}
      disabled={isLoading}
      autoHighlight
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={getPresetOptionLabel}
      groupBy={(preset) => getCalculateFeaturesPresetGroup(preset, groupContext)}
      filterOptions={(filteredOptions, state) => {
        const query = state.inputValue.trim().toLowerCase();
        if (!query) {
          return filteredOptions;
        }

        return filter(filteredOptions, (preset) =>
          getCalculateFeaturesPresetFilterText(preset).toLowerCase().includes(query)
        );
      }}
      onChange={(_event, preset) => {
        if (preset) {
          onSelectPreset(preset);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Calculate-features preset"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={18.5} title="Loading calculate features presets..." />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, preset) => (
        <li {...props} key={preset.id}>
          <Grid container direction="row" spacing={1} alignItems="center">
            <Grid item>
              <Typography variant="body1">{getPresetOptionLabel(preset)}</Typography>
            </Grid>
            {preset.isPublished && (
              <Grid item>
                <Chip size="small" label="Published" color="success" />
              </Grid>
            )}
            {preset.isGlobal && (
              <Grid item>
                <Chip size="small" label="Global" variant="outlined" />
              </Grid>
            )}
            <Grid item>
              <Typography variant="body2">{formatDateTimeForUser(preset.createdAt)}</Typography>
            </Grid>
            {preset.updatedAt && (
              <Grid item>
                <Typography variant="body2">(updated at: {formatDateTimeForUser(preset.updatedAt)})</Typography>
              </Grid>
            )}
            <Grid item>
              {isLoadingStainTypeOptions ? (
                <CircularProgress size={16} />
              ) : (
                <Typography variant="caption">
                  (
                  {join(
                    map(preset.stains, (stain) => stainTypeIdToDisplayName(stain)),
                    ', '
                  )}
                  )
                </Typography>
              )}
            </Grid>
          </Grid>
        </li>
      )}
    />
  );
};
